import { type ComputedRef, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { useDrainPages } from '@/composables/useDrainPages'
import { fetchBusinessServiceCategories } from '@/api/businessServiceCategories'
import { serviceCategoriesPollingInterval } from '@/config/api'
import { formatDuration } from '@/utils/formatDuration'
import { useAllBusinessServiceTypes } from '@/composables/useAllBusinessServiceTypes'

export interface ServiceTypeOption {
  // One selectable service type inside a category group.

  id: string
  // Service type id (UUID7).

  name: string
  // Description, falling back to a generic id-based label when empty.

  duration: string
  // Formatted duration (e.g. `1h 30m`); empty when unparseable.
}

export interface ServiceTypeOptionGroup {
  // Service types under one parent category, as in the service form's picker.

  categoryId: string
  // Parent category id (UUID7).

  categoryName: string
  // Category display name, falling back to a generic id-based label.

  types: ServiceTypeOption[]
  // Options in this category.
}

/**
 * Service types grouped by their parent category with resolved names — the
 * data behind the grouped service-type dropdowns (same shape the service
 * forms build inline). Each catalog is only fetched when its `enabled` flag is
 * true (`BusinessServiceTypesView` / `BusinessServiceNamesView`), so a missing
 * role never triggers a 403 redirect.
 */
export function useServiceTypeGroups(options: {
  typesEnabled: ComputedRef<boolean>
  categoriesEnabled: ComputedRef<boolean>
}) {
  const { t } = useI18n()
  const { businessServiceTypes } = useAllBusinessServiceTypes({ enabled: options.typesEnabled })

  const categoriesQuery = useInfiniteQuery({
    queryKey: ['businessServiceCategories'],
    queryFn: ({ pageParam }) => fetchBusinessServiceCategories(pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
    refetchInterval: serviceCategoriesPollingInterval,
    refetchIntervalInBackground: false,
    enabled: options.categoriesEnabled,
  })
  useDrainPages(categoriesQuery)

  const groups = computed<ServiceTypeOptionGroup[]>(() => {
    const names = new Map<string, string>()
    for (const page of categoriesQuery.data.value?.pages ?? []) {
      for (const item of page.items ?? []) names.set(item.id, item.name)
    }
    const byCategory = new Map<string, ServiceTypeOptionGroup>()
    for (const type of businessServiceTypes.value) {
      const categoryId = type.business_service_name_id
      let group = byCategory.get(categoryId)
      if (!group) {
        group = {
          categoryId,
          categoryName:
            names.get(categoryId) ?? t('tabs.services.categoryFallback', { id: categoryId }),
          types: [],
        }
        byCategory.set(categoryId, group)
      }
      group.types.push({
        id: type.id,
        name: type.description?.trim() || t('tabs.services.serviceTypeFallback', { id: type.id }),
        duration: formatDuration(type.duration) ?? '',
      })
    }
    return Array.from(byCategory.values())
  })

  return { groups }
}
